import { blankRow, blankSubCell } from "../factories";
import { NotationContent } from "../model";
import { insertAt, mapRow, mapSection, removeAt } from "./internal";

/** Row-level operations. All pure: `(content, args) => content`. */

export interface AddRowArgs {
    sectionIndex: number;
    position?: number;
    taalCount: number;
    vectorLength: number;
}

export function addRow<T extends NotationContent>(
    content: T,
    { sectionIndex, position, taalCount, vectorLength }: AddRowArgs
): T {
    return mapSection(content, sectionIndex, (section) => ({
        ...section,
        rows: insertAt(section.rows, blankRow(taalCount, vectorLength), position),
    }));
}

export function deleteRow<T extends NotationContent>(
    content: T,
    args: { sectionIndex: number; rowIndex: number }
): T {
    return mapSection(content, args.sectionIndex, (section) => {
        // A section always keeps at least one row.
        if (section.rows.length <= 1) return section;
        return { ...section, rows: removeAt(section.rows, args.rowIndex) };
    });
}

export function moveRowUp<T extends NotationContent>(
    content: T,
    args: { sectionIndex: number; rowIndex: number }
): T {
    return mapSection(content, args.sectionIndex, (section) => {
        const { rowIndex } = args;
        if (rowIndex > 0 && rowIndex < section.rows.length) {
            const rows = [...section.rows];
            const tmp = rows[rowIndex];
            rows[rowIndex] = rows[rowIndex - 1];
            rows[rowIndex - 1] = tmp;
            return { ...section, rows };
        }
        return section;
    });
}

export function moveRowDown<T extends NotationContent>(
    content: T,
    args: { sectionIndex: number; rowIndex: number }
): T {
    return mapSection(content, args.sectionIndex, (section) => {
        const { rowIndex } = args;
        if (rowIndex >= 0 && rowIndex < section.rows.length - 1) {
            const rows = [...section.rows];
            const tmp = rows[rowIndex];
            rows[rowIndex] = rows[rowIndex + 1];
            rows[rowIndex + 1] = tmp;
            return { ...section, rows };
        }
        return section;
    });
}

/** Rotate the matras one step left (first vector moves to the end). */
export function rotateRowLeft<T extends NotationContent>(
    content: T,
    args: { sectionIndex: number; rowIndex: number }
): T {
    return mapRow(content, args.sectionIndex, args.rowIndex, (row) => {
        if (row.vectors.length === 0) return row;
        const [first, ...rest] = row.vectors;
        return { ...row, vectors: [...rest, first] };
    });
}

/** Rotate the matras one step right (last vector moves to the front). */
export function rotateRowRight<T extends NotationContent>(
    content: T,
    args: { sectionIndex: number; rowIndex: number }
): T {
    return mapRow(content, args.sectionIndex, args.rowIndex, (row) => {
        if (row.vectors.length === 0) {
            // The original threw here; there is no UI path that reaches it.
            throw new Error("Insufficient vectors in row.");
        }
        const last = row.vectors[row.vectors.length - 1];
        return { ...row, vectors: [last, ...row.vectors.slice(0, -1)] };
    });
}

/**
 * Add an extra, row-specific track. Every vector in the row gains one more
 * cell, sized to match the existing subcell count.
 */
export function addAdditionalVectorElementInARow<T extends NotationContent>(
    content: T,
    args: { sectionIndex: number; rowIndex: number; count?: number }
): T {
    const count = args.count ?? 1;
    return mapRow(content, args.sectionIndex, args.rowIndex, (row) => {
        const vectors = row.vectors.map((vector) => {
            const subCellCount = vector.cells[0]?.length ?? 1;
            const newCell = Array.from({ length: subCellCount }, blankSubCell);
            return { ...vector, cells: [...vector.cells, newCell] };
        });
        return {
            ...row,
            vectors,
            options: {
                ...row.options,
                additionalRowVectorElements:
                    row.options.additionalRowVectorElements + count,
            },
            // NOTE: exactly one name and one render type are pushed regardless
            // of `count`, while the counter above moves by `count`. Preserved
            // as-is; the two only diverge when a caller passes count !== 1,
            // which no caller does. `removeAdditionalVectorElementInARow`
            // drops exactly one of each for the same reason.
            subRowNames: [
                ...row.subRowNames,
                `Track #${(vectors[0]?.cells.length ?? 1)}`,
            ],
            renderMethod: [...row.renderMethod, "simple" as const],
        };
    });
}

export function removeAdditionalVectorElementInARow<T extends NotationContent>(
    content: T,
    args: { sectionIndex: number; rowIndex: number; count?: number }
): T {
    const count = args.count ?? 1;
    return mapRow(content, args.sectionIndex, args.rowIndex, (row) => {
        if (row.options.additionalRowVectorElements <= 0) return row;
        return {
            ...row,
            vectors: row.vectors.map((vector) => ({
                ...vector,
                cells: vector.cells.slice(0, -1),
            })),
            options: {
                ...row.options,
                additionalRowVectorElements:
                    row.options.additionalRowVectorElements - count,
            },
            // `subRowNames` and `renderMethod` are parallel — one entry per
            // row-specific extra track — so both shrink together. Dropping the
            // name alone leaves a stale render type behind, and the resulting
            // length mismatch fails the structural check on the save path:
            // every later autosave, manual save and unload flush of that
            // document is then rejected and the session's work is lost.
            // Exactly one entry is dropped regardless of `count`, mirroring
            // the add side (see the NOTE there).
            subRowNames: row.subRowNames.slice(0, -1),
            renderMethod: row.renderMethod.slice(0, -1),
        };
    });
}
